/**
 * Deterministic string hash used across the app wherever a stable
 * pseudo-random value must be derived from an id or a text input:
 * artwork hues, licence numbers, genre-compatibility scores, and the
 * simulated Music DNA analysis.
 *
 * This is the classic djb2-style shift-add-xor hash. It is NOT a
 * cryptographic hash and must never be used for anything security-bearing —
 * its only job is to turn a string into a stable number so the UI renders the
 * same colours and scores on every visit.
 */
export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Stable hue (0-359) for a song's gradient artwork.
 *
 * Song ids used to be integers, so the views did `id % 360` directly. With
 * string ids that arithmetic yields NaN, so the hue is derived through
 * hashString - the same approach the Vault views already used for their
 * string ids.
 */
export const idHue = (id: string | number, multiplier = 1): number =>
  (hashString(String(id)) * multiplier) % 360;
